import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { deepCopy } from '@engineering11/utility';
import { isE11Error, STANDARD_ERROR_MESSAGE } from '@engineering11/web-api-error';
import * as i1 from '@engineering11/ui-lib/e11-notifications';
import * as i2 from '@ngx-translate/core';

/**
 * Automatically translates notifications
 * Helps pop notifications from errors in a standard way
 * ? Named "helper" instead of service because we already have classes called NotificationService and because it's in a helper lib that wires other libraries together
 */
class NotificationHelper {
    constructor(notificationsService, translate) {
        this.notificationsService = notificationsService;
        this.translate = translate;
    }
    popNotification(notification) {
        const translatedNotification = this.translateNotification(notification);
        this.notificationsService.popNotificationMessage(translatedNotification);
    }
    popNotificationFromError(err, overrides) {
        const title = overrides?.title ?? 'An Error Has Occurred';
        const type = overrides?.type ?? 'warning';
        const autoClose = overrides?.autoClose ?? false;
        if (!isE11Error(err))
            return this.popNotification({ type, title, message: STANDARD_ERROR_MESSAGE, autoClose });
        const message = overrides?.message ?? err.errorData.title ?? STANDARD_ERROR_MESSAGE;
        this.popNotification({ type, title, message, autoClose });
    }
    translateNotification(notification) {
        const translatedNotification = deepCopy(notification);
        this.translate.get(translatedNotification.title).subscribe((res) => {
            translatedNotification.title = res;
        });
        this.translate.get(translatedNotification.message).subscribe((res) => {
            translatedNotification.message = res;
        });
        return translatedNotification;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NotificationHelper, deps: [{ token: i1.E11NotificationsService }, { token: i2.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NotificationHelper, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NotificationHelper, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.E11NotificationsService }, { type: i2.TranslateService }] });
/**
 * @deprecated use NotificationHelper - the more distinct name is to disambiguate from other NotificationServices
 */
class NotificationService extends NotificationHelper {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NotificationService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NotificationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NotificationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * A convenience class which lets the NotificationHelper conform to IErrorAlertService
 */
class E11AlertNotificationService {
    constructor(notificationHelper) {
        this.notificationHelper = notificationHelper;
    }
    alertUser(error) {
        return this.notificationHelper.popNotificationFromError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AlertNotificationService, deps: [{ token: NotificationHelper }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AlertNotificationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AlertNotificationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: NotificationHelper }] });

class WebUiHelpersModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WebUiHelpersModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: WebUiHelpersModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WebUiHelpersModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WebUiHelpersModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11AlertNotificationService, NotificationHelper, NotificationService, WebUiHelpersModule };
